import { reaction, autorun } from 'mobx';

/**
 * Создает логгер для store
 * @param {string} storeName - Имя store
 * @returns {Function} Функция логгера
 */
export const createStoreLogger = (storeName) => {
  return (action, ...args) => {
    console.log(`[${storeName}] ${action}:`, ...args);
  };
};

/**
 * Валидация диапазона дат
 * @param {string} startDate - Дата начала
 * @param {string} endDate - Дата окончания
 * @param {string} minDate - Минимальная допустимая дата
 * @param {string} maxDate - Максимальная допустимая дата
 * @returns {boolean} Результат валидации
 */
export const validateDateRange = (startDate, endDate, minDate, maxDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const min = new Date(minDate);
  const max = new Date(maxDate);
  
  return start >= min && end <= max && start <= end;
};

/**
 * Функция debounce для оптимизации производительности
 * @param {Function} func - Функция для debounce
 * @param {number} wait - Время ожидания в миллисекундах
 * @returns {Function} Debounced функция
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Функция throttle для ограничения частоты вызовов
 * @param {Function} func - Функция для throttle
 * @param {number} limit - Лимит времени в миллисекундах
 * @returns {Function} Throttled функция
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Создает реакцию на изменения в store
 * @param {Object} store - MobX store
 * @param {Function} dataSelector - Функция для выбора данных
 * @param {Function} effect - Эффект при изменении данных
 * @param {Object} options - Опции реакции
 * @returns {Function} Функция для отмены реакции
 */
export const createStoreReaction = (store, dataSelector, effect, options = {}) => {
  return reaction(
    dataSelector,
    effect,
    {
      fireImmediately: true,
      ...options
    }
  );
};

/**
 * Создает autorun для автоматического выполнения эффектов
 * @param {Function} effect - Эффект для выполнения
 * @returns {Function} Функция для отмены autorun
 */
export const createStoreAutorun = (effect) => {
  return autorun(effect);
};

/**
 * Утилита для глубокого клонирования объектов
 * @param {*} obj - Объект для клонирования
 * @returns {*} Клонированный объект
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime());
  if (obj instanceof Array) return obj.map(item => deepClone(item));
  if (typeof obj === 'object') {
    const clonedObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
};

/**
 * Утилита для сравнения объектов
 * @param {*} obj1 - Первый объект
 * @param {*} obj2 - Второй объект
 * @returns {boolean} Результат сравнения
 */
export const deepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;
  if (obj1 == null || obj2 == null) return false;
  if (obj1.constructor !== obj2.constructor) return false;
  
  if (obj1 instanceof Date && obj2 instanceof Date) {
    return obj1.getTime() === obj2.getTime();
  }
  
  if (obj1 instanceof Array && obj2 instanceof Array) {
    if (obj1.length !== obj2.length) return false;
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i])) return false;
    }
    return true;
  }
  
  if (typeof obj1 === 'object' && typeof obj2 === 'object') {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    
    if (keys1.length !== keys2.length) return false;
    
    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!deepEqual(obj1[key], obj2[key])) return false;
    }
    return true;
  }
  
  return false;
};

/**
 * Утилита для создания уникального ID
 * @returns {string} Уникальный ID
 */
export const generateId = () => {
  return Math.random().toString(36).substr(2, 9);
};

/**
 * Утилита для форматирования даты
 * @param {string|Date} date - Дата для форматирования
 * @param {string} format - Формат даты
 * @returns {string} Отформатированная дата
 */
export const formatDate = (date, format = 'YYYY-MM-DD') => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  
  return format
    .replace('YYYY', year)
    .replace('MM', month)
    .replace('DD', day);
};

/**
 * Утилита для валидации email
 * @param {string} email - Email для валидации
 * @returns {boolean} Результат валидации
 */
export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

/**
 * Утилита для валидации URL
 * @param {string} url - URL для валидации
 * @returns {boolean} Результат валидации
 */
export const validateUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};
